/* IU Search 1.1.0: controlled clothing associations; native Shopify pagination/filters. */
(() => {
  'use strict';
  const fold = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const materials = [
    ['casmir', 'cashmir', 'cashmere', 'kaschmir', 'cașmir', 'caşmir'],
    ['matase', 'mătase', 'silk', 'seide'], ['lana', 'lână', 'lâna', 'wool', 'wolle'],
    ['merino', 'merinos'], ['alpaca', 'alpacă', 'alpakka'], ['angora'], ['mohair'],
    ['in', 'linen', 'lin', 'leinen'], ['bumbac', 'cotton', 'baumwolle'],
    ['vascoza', 'vâscoză', 'viscoza', 'viscoză', 'viscose', 'viskose', 'rayon', 'raion'],
    ['modal'], ['lyocell', 'tencel'], ['poliester', 'polyester'],
    ['poliamida', 'poliamidă', 'poliamid', 'polyamide', 'nylon'],
    ['elastan', 'elastane', 'elastano', 'spandex', 'lycra'],
    ['acril', 'acrylic', 'acrilic', 'polyacryl'], ['ramie', 'rami'], ['cupro'],
    ['canepa', 'cânepă', 'hemp'], ['bambus', 'bamboo'], ['acetat', 'acetate'],
    ['piele', 'leather'], ['suede', 'velur']
  ];
  const articleWords = [
    ['bluza', 'bluză', 'bluze', 'blouse', 'blouses'], ['pulover', 'pulovere', 'sweater', 'sweaters', 'jumper'],
    ['camasa', 'cămașă', 'cămaşa', 'cămașa', 'camasi', 'cămăși'],
    ['tricou', 'tricouri', 't-shirt', 'tshirt'], ['pantalon', 'pantaloni', 'trousers'], ['blugi', 'jeans', 'jeansi'],
    ['fusta', 'fustă', 'fuste', 'skirt', 'skirts'], ['rochie', 'rochii', 'dress', 'dresses'],
    ['geaca', 'geacă', 'geci', 'jacheta', 'jachetă', 'jachete', 'jacket', 'jackets'],
    ['sacou', 'sacouri', 'blazer', 'blazere', 'blazers'], ['hanorac', 'hanorace', 'hoodie', 'hoodies'],
    ['palton', 'paltoane', 'overcoat'], ['vesta', 'vestă', 'veste', 'vest', 'waistcoat'],
    ['cardigan', 'cardigane', 'cardigans'], ['colant', 'colanti', 'colanți', 'legging', 'leggings']
  ];
  const colors = [
    ['negru', 'neagră', 'negri', 'negre', 'black'], ['alb', 'albă', 'albi', 'albe', 'white'],
    ['gri', 'gray', 'grey'], ['bleumarin', 'navy'], ['maro', 'brown'], ['bej', 'beige'],
    ['roz', 'pink'], ['rosu', 'roșu', 'rosie', 'roșie', 'rosii', 'roșii', 'red'],
    ['albastru', 'albastră', 'albastri', 'albaștri', 'albastre', 'blue'], ['verde', 'verzi', 'green']
  ];
  // Exact brand spelling variants, not "similar" brands or generic words such as polo.
  const brands = [
    ['Levis', "Levi's", 'Levi’s'], ['Massimo Dutti', 'MassimoDutti'],
    ['The North Face', 'Thenorthface', 'North Face'],
    ["Marc O'Polo", 'Marc O’Polo', 'Marc O Polo', 'MarcOPolo'],
    ['Ralph Lauren', 'RalphLauren'], ['Calvin Klein', 'CalvinKlein'],
    ['Tommy Hilfiger', 'TommyHilfiger'], ['Tom Tailor', 'TomTailor'],
    ['Jack&Jones', 'Jack & Jones', 'Jack and Jones', 'JackJones'],
    ['Scotch&Soda', 'Scotch & Soda', 'Scotch and Soda', 'ScotchSoda'],
    ['Hugo Boss', 'HugoBoss'], ['Fjällräven', 'Fjallraven', 'Fjall Raven', 'Fjäll Räven'],
    ['Karl Kani', 'KarlKani', 'Karl Kanni'], ['Seidensticker', 'Seidensicker']
  ];
  const quote = text => '"' + text.replace(/[\\"]/g, '\\$&') + '"';
  const tokenize = text => text.match(/[\p{L}\p{N}]+(?:%|[-'’][\p{L}\p{N}]+)*/gu) || [];
  const tokenKey = text => fold(text).replace(/’/g, "'");
  const clause = (aliases, fields) => '(' + [...new Set(aliases)].flatMap(alias =>
    fields.map(field => field + ':' + quote(alias))).join(' OR ') + ')';
  const identityFields = ['title', 'product_type'];
  const contentFields = ['title', 'body'];
  const brandPatterns = brands.flatMap(aliases => aliases.map(alias => ({aliases, tokens: tokenize(alias).map(tokenKey)})))
    .sort((a, b) => b.tokens.length - a.tokens.length);
  function articleClause(aliases) {
    const direct = clause(aliases, identityFields);
    if (aliases[0] !== 'pulover') return direct;
    // A blouse is a sweater substitute only with explicit knitting evidence.
    // Outfit suggestions in the body never establish an article's identity.
    const cardigans = clause(articleWords.find(group => group[0] === 'cardigan'), identityFields);
    const blouses = clause(articleWords[0], identityFields);
    const knitted = '(title:tricot* OR body:tricot* OR title:knit* OR body:knit*)';
    return '(' + direct + ' OR ' + cardigans + ' OR (' + blouses + ' AND ' + knitted + '))';
  }
  function normalize(query) {
    const text = query.trim().replace(/\s+/g, ' ');
    // Honor an explicitly typed advanced query; never reinterpret operators/fields.
    if (/[":()\\]|\b(?:AND|OR|NOT)\b|(?:^|\s)-[\p{L}\p{N}]/u.test(text)) return {query: text, material: false, strict: true};
    const tokens = tokenize(text);
    const groups = [], consumed = new Set(), brandClauses = [];
    let associated = false;
    tokens.forEach((token, index) => {
      if (consumed.has(index)) return;
      const match = brandPatterns.find(pattern => pattern.tokens.every((key, offset) =>
        !consumed.has(index + offset) && tokenKey(tokens[index + offset] || '') === key));
      if (!match) return;
      match.tokens.forEach((key, offset) => consumed.add(index + offset));
      const value = clause(match.aliases, ['title', 'vendor']);
      if (!brandClauses.includes(value)) brandClauses.push(value);
      associated = true;
    });
    tokens.forEach((token, index) => {
      if (consumed.has(index)) return;
      const word = fold(token);
      const group = materials.find(aliases => aliases.some(alias => fold(alias) === word));
      if (!group) return;
      // Romanian "în" is a preposition. Plain "in" is a fiber only on its own,
      // at the end ("camasa din in"), or next to a percentage, not "in stil ...".
      if (word === 'in' && (token.toLowerCase() === 'în' ||
          (tokens.length > 1 && index !== tokens.length - 1 && !/%$/.test(tokens[index - 1] || '')))) return;
      consumed.add(index);
      if (!groups.includes(group)) groups.push(group);
    });
    const stopwords = new Set(['din', 'de', 'cu', 'si', 'in', 'the', 'from', 'with', 'and']);
    const words = tokens.filter((token, index) => !consumed.has(index) && !stopwords.has(fold(token)));
    const fields = ['title', 'body', 'vendor', 'product_type', 'variants.title', 'variants.sku', 'variants.barcode'];
    const remaining = words.map((token, index) => {
      const article = articleWords.find(aliases => aliases.some(alias => fold(alias) === fold(token)));
      if (article) { associated = true; return articleClause(article); }
      const color = colors.find(aliases => aliases.some(alias => fold(alias) === fold(token)));
      if (color) { associated = true; return clause(color, contentFields); }
      const aliases = [token];
      // Only a final plain word can be a prefix; materials are always complete words.
      const prefix = !groups.length && aliases.length === 1 && index === words.length - 1 && /^[\p{L}]{3,}$/u.test(token);
      return '(' + aliases.flatMap(alias => fields.map(field => field + ':' + (prefix ? alias + '*' : quote(alias)))).join(' OR ') + ')';
    });
    const clauses = groups.map(aliases => clause(aliases, contentFields));
    return {query: [...brandClauses, ...remaining, ...clauses].join(' AND ') || text,
      material: groups.length > 0, associated, strict: true};
  }
  function url(query, base = '/search', entries) {
    const target = new URL(base, window.location.origin);
    if (entries) target.search = new URLSearchParams(entries).toString();
    const normalized = normalize(query);
    target.searchParams.set('q', normalized.query);
    target.searchParams.set('iu_q', query.trim());
    target.searchParams.set('type', 'product');
    target.searchParams.set('options[prefix]', normalized.strict ? 'none' : 'last');
    target.searchParams.set('options[unavailable_products]',
      target.searchParams.get('iu_availability') === 'all' ? 'last' : 'hide');
    return target;
  }
  window.IUStoreSearch = {normalize, url};
  function preserveContext(target) {
    const current = new URL(window.location.href);
    ['iu_q', 'iu_availability', 'sort_by'].forEach(name => {
      if (!target.searchParams.has(name) && current.searchParams.has(name)) target.searchParams.set(name, current.searchParams.get(name));
    });
    return url(target.searchParams.get('iu_q') || target.searchParams.get('q') || '', target.href);
  }
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    const input = form.querySelector('input[name="q"]:not([type="hidden"])');
    if (!input || new URL(form.action).pathname !== '/search' || event.defaultPrevented) return;
    if (!input.value.trim()) { event.preventDefault(); input.focus(); return; }
    event.preventDefault();
    window.location.assign(url(input.value, form.action, new FormData(form)).href);
  });
  document.addEventListener('change', event => {
    const facets = event.target.closest('.iu-search-page #CollectionFiltersForm');
    if (facets) {
      // Reload the complete native result page so count, sorting and filters agree.
      // The theme's collection-only AJAX renderer doesn't update the search header.
      event.stopImmediatePropagation();
      const target = new URL(window.location.href);
      target.search = new URLSearchParams(new FormData(facets)).toString();
      window.location.assign(preserveContext(target).href);
      return;
    }
    if (!event.target.matches('[data-iu-search-sort], [data-iu-search-availability]')) return;
    const target = new URL(window.location.href);
    target.searchParams.set(event.target.name, event.target.value);
    target.searchParams.delete('page');
    window.location.assign(url(target.searchParams.get('iu_q') || target.searchParams.get('q') || '', target.href).href);
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('.iu-search-page .js-facet-remove, .iu-search-page .pagination a[href]');
    if (!link) return;
    if (!/^https?:/.test(link.href) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(preserveContext(new URL(link.href)).href);
  }, true);
  function initialize() {
    const current = new URL(window.location.href);
    const section = document.querySelector('[data-section-type="search"]');
    if (section) {
      const query = current.searchParams.get('iu_q') || current.searchParams.get('q') || '';
      if (query) {
        const target = url(query, current.href);
        if (target.search !== current.search) { window.location.replace(target.href); return; }
      }
      document.querySelectorAll('form[action="/search"] input[name="q"]:not([type="hidden"])').forEach(input => { input.value = query; });
      section.querySelectorAll('[data-iu-search-term]').forEach(element => { element.textContent = query; });
      const note = section.querySelector('[data-iu-material-note]');
      if (note) note.hidden = !normalize(query).material;
      const availability = section.querySelector('[data-iu-search-availability]');
      if (availability) availability.value = current.searchParams.get('iu_availability') === 'all' ? 'all' : 'stock';
      section.querySelectorAll('.pagination a[href]').forEach(link => {
        if (/^https?:/.test(link.href)) link.href = preserveContext(new URL(link.href)).href;
      });
      section.querySelectorAll('#CollectionFiltersForm').forEach(form => {
        ['iu_q', 'iu_availability', 'options[unavailable_products]'].forEach(name => {
          if (!current.searchParams.has(name)) return;
          let input = form.querySelector('input[name="' + name + '"]');
          if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = name; form.append(input); }
          input.value = current.searchParams.get(name);
        });
      });
      document.title = query ? 'Căutare: ' + query + ' – Între Umerașe' : 'Căutare – Între Umerașe';
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, {once: true});
  else initialize();
})();
